<template>
  <Filters
    v-model:filtersValues="filtersValues"
    :publicationState
    :resetFilters
    :submitFilters
  />
  <OrderingFilters
    v-model:areAllListingsExpanded="areAllListingsExpanded"
    v-model:filterValues="filtersValues"
    :active-publication-state="publicationState"
    class="mb-24 mt-24 md:mb-32 md:mt-32"
    :count="listingsCount"
    :expand-all-listings="handleExpandAllListings"
    :listings-per-page="filtersValues.pageSize"
    :sort-by="filtersValues.orderBy"
    :submitFilters
  />
  <QuickFilters v-model:filtersValues="filtersValues" :submitFilters />
  <RevCard v-if="status === 'pending'">
    <RevLoadingScreen
      class="py-72"
      :text="i18n(translations.loading)"
      variant="light"
    />
  </RevCard>
  <template v-else-if="listings.length">
    <ListingsTable
      :areAllListingsExpanded
      :listings
      :publicationState
      @refresh-listings="refreshListings"
    />
    <Pagination
      :hasNextPage="Boolean(nextCursor)"
      :hasPreviousPage="Boolean(prevCursor)"
      :onNextPage="fetchNextPage"
      :onPreviousPage="fetchPreviousPage"
    />
  </template>
  <EmptyState v-else :publicationState />
  <ListingDetailsDrawer />
  <DealsInfoDrawer v-if="isDealsBannerEnabled" />
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'
import { STORAGE_VIEW_TYPE_NAME } from '~/scopes/listings/Listings.constants'

import EmptyState from '../../../index/components/EmptyState/EmptyState.vue'
import ListingDetailsDrawer from '../../../index/components/ListingDetailsDrawer/ListingDetailsDrawer.vue'
import { useListings } from '../../composables/useListings'
import { useListingsTracking } from '../../composables/useListingsTracking'
import DealsInfoDrawer from '../DealsInfoDrawer/DealsInfoDrawer.vue'
import Filters from '../Filters/Filters.vue'
import QuickFilters from '../Filters/components/QuickFilters/QuickFilters.vue'
import ListingsTable from '../ListingsTable/ListingsTable.vue'
import { VIEWS } from '../OrderingFilters/OrderingFilters.constants'
import OrderingFilters from '../OrderingFilters/OrderingFilters.vue'
import Pagination from '../Pagination/Pagination.vue'

import translations from './Tab.translations'

const i18n = useI18n()

const {
  publicationState,
  getListings,
  listings,
  nextCursor,
  prevCursor,
  listingsCount,
  filtersValues,
  resetFilters,
  submitFilters,
  direction,
} = useListings()
const { activeMarkets } = useSellerInfoStore()
// Use activeMarkets.length instead of hasOnlyOneMarket from the store
// because UK sellers (previously in EU) have non-empty potentialMarkets.
// And since the hasOnlyOneMarket relies on the potentialMarkets + activeMarkets length info,
// it causes hasOnlyOneMarket to return false incorrectly in the UK case.
const hasOnlySingleMarket = activeMarkets.length === 1

const { isFeatureEnabled } = useBusinessFeaturesStore()
const isDealsBannerEnabled = isFeatureEnabled('isDealsBannerEnabled')

const localStorage = useLocalStorage()
const tracking = useTracking()
const { formatListingToTrackingEvent } = useListingsTracking()

// Single market view should have all listings expanded by default
const defaultAreAllListingsExpandedState = hasOnlySingleMarket

const areAllListingsExpanded = ref(
  localStorage.getItem(STORAGE_VIEW_TYPE_NAME)
    ? localStorage.getItem(STORAGE_VIEW_TYPE_NAME) === VIEWS.EXPANDED
    : defaultAreAllListingsExpandedState,
)

function handleExpandAllListings(value: boolean) {
  if (value) {
    areAllListingsExpanded.value = true
    localStorage.setItem(STORAGE_VIEW_TYPE_NAME, VIEWS.EXPANDED)
    tracking.trackClick({
      page_type: 'listings',
      zone: 'sort_display',
      name: 'display_type',
      value: VIEWS.EXPANDED,
    })
    listings.value.forEach((listing) => {
      tracking.trackBoSellerListingImpression({
        page_type: 'listings',
        zone: 'listing_details',
        name: 'listing_impression',
        ...formatListingToTrackingEvent(listing),
      })
    })
  } else {
    areAllListingsExpanded.value = false
    tracking.trackClick({
      page_type: 'listings',
      zone: 'sort_display',
      name: 'display_type',
      value: VIEWS.SIMPLE,
    })
    localStorage.setItem(STORAGE_VIEW_TYPE_NAME, VIEWS.SIMPLE)
  }
}

const { refreshListings, status } = getListings()

async function fetchNextPage() {
  if (!nextCursor.value) {
    return
  }

  direction.value = 'next'
  refreshListings()
}

async function fetchPreviousPage() {
  if (!prevCursor.value) {
    return
  }

  direction.value = 'previous'
  refreshListings()
}
</script>
